import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField, MenuItem } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";

const INITIAL_STATE = {
  integrationId: "",
  vehicleId: "",
  driverId: "",
  shipmentId: "",
  externalSystemId: "",
  statusUpdate: "",
  documentType: "",
  documentUrl: "",
  comment: "",
  errorDetail: "",
  location: {
    lat: 0,
    lon: 0,
  },
  timestamp: new Date(),
  status: "Pending",
  isActive: true,
};

const DataIntegrationForm = ({
  isBusy = false,
  dataIntegrationDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  // For Vehicles
  const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] =
    useLazyGetDDLVehicleListQuery();

  // For Requests
  const [fetchRequests, { data: requests, isLoading: loadingRequests }] =
    useLazyGetDDLShipmentListQuery();

  // For Drivers
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();


  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
    },
  });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      driverId: data?.driverId?._id,
      shipmentId: data?.shipmentId?._id,
      vehicleId: data?.vehicleId?._id,
    };
    onSubmit(formattedData);
  };

  // Reset form when dataIntegrationDetail changes
  React.useEffect(() => {
    if (dataIntegrationDetail) {
      reset({ ...INITIAL_STATE, ...dataIntegrationDetail });
    }
  }, [dataIntegrationDetail, reset]);



  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id });
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchVehicles, viewEntity,]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchRequests({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchRequests, viewEntity,]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchDrivers, viewEntity]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("shipmentId", "")
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* shipment ID */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select a shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="shipment ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data)
                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={requests || []}
                  loading={loadingRequests}
                  loadingText="Loading Requests id..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>

          {/* Assigned Vehicle ID */}
          <Grid item md={6}>
            <Controller
              name="vehicleId"
              control={control}
              rules={{ required: "Please select Vehicle ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) => option?.vehicle_id ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={vehicles || []}
                  loading={loadingVehicles}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicleId?.message}
                  error={!!errors?.vehicleId}
                />
              )}
            />
          </Grid>
          

          {/*  Driver ID */}
          <Grid item md={6}>
            <Controller
              name="driverId"
              control={control}
              rules={{ required: "Please select Driver ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Driver ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) => option?.driverId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={drivers || []}
                  loading={loadingDrivers}
                  loadingText="Loading Drivers..."
                  helperText={errors?.driverId?.message}
                  error={!!errors?.driverId}
                />
              )}
            />
          </Grid>

          

          {/* External System Id */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="externalSystemId"
              control={control}
              rules={{ required: "External System Id is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  required
                  fullWidth
                  label="External System Id"
                  size="small"
                  error={!!errors.externalSystemId}
                  helperText={errors.externalSystemId?.message}
                >
                  <MenuItem value="Third-party logistics">
                    Third-party logistics
                  </MenuItem>
                  <MenuItem value="Warehouse system">Warehouse system</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {/* Document Type */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="documentType"
              control={control}
              rules={{ required: "Document Type is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  required
                  fullWidth
                  label="Document Type"
                  size="small"
                  error={!!errors.documentType}
                  helperText={errors.documentType?.message}
                >
                  <MenuItem value="Invoice">Invoice</MenuItem>
                  <MenuItem value="Permit">Permit</MenuItem>
                  <MenuItem value="Delivery confirmation">
                    Delivery confirmation
                  </MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {/* Document URL */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="documentUrl"
              control={control}
              rules={{ required: "Document URL is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Document URL"
                  size="small"
                  error={!!errors.documentUrl}
                  helperText={errors.documentUrl?.message}
                />
              )}
            />
          </Grid>

          {/* Comment */}
          <Grid item xs={12}>
            <Controller
              name="comment"
              control={control}
              rules={{ required: "Comment is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  multiline
                  rows={3}
                  label="Comment"
                  size="small"
                  error={!!errors.comment}
                  helperText={errors.comment?.message}
                />
              )}
            />
          </Grid>

          {/* Error Detail */}
          <Grid item xs={12}>
            <Controller
              name="errorDetail"
              control={control}
              rules={{ required: "Error Detail is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  multiline
                  rows={3}
                  label="Error Detail"
                  size="small"
                  error={!!errors.errorDetail}
                  helperText={errors.errorDetail?.message}
                />
              )}
            />
          </Grid>

          {/* Location - Latitude */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="location.lat"
              control={control}
              rules={{ required: "Latitude is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Latitude"
                  size="small"
                  type="number"
                  error={!!errors.location?.lat}
                  helperText={errors.location?.lat?.message}
                />
              )}
            />
          </Grid>

          {/* Location - Longitude */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="location.lon"
              control={control}
              rules={{ required: "Longitude is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Longitude"
                  size="small"
                  type="number"
                  error={!!errors.location?.lon}
                  helperText={errors.location?.lon?.message}
                />
              )}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Status"
                  size="small"
                  error={!!errors.status}
                  helperText={errors.status?.message}
                >
                  <MenuItem value="Successful">Successful</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="statusUpdate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Status"
                  size="small"
                  error={!!errors.statusUpdate}
                  helperText={errors.statusUpdate?.message}
                >
                  <MenuItem value="Shipment in transit">Shipment in transit</MenuItem>
                  <MenuItem value="Document updated">Document updated</MenuItem>

                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {dataIntegrationDetail ? "Update Integration" : "Add Integration"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};
export default DataIntegrationForm;
